import { useCallback, useState } from 'react';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Tabs } from '../../../components/ui/Tabs';
import { Spinner, StateBlock } from '../../../components/ui/Feedback';
import { useToast } from '../../../components/ui/ToastProvider';
import { api, errorMessage } from '../../../lib/api';
import { useAsync } from '../../../lib/hooks';
import { formatDate, formatNumber } from '../../../lib/format';
import { mailSummaryMessage } from '../../../lib/mail';
import { RFP_VENDOR_STATUS_META } from '../../../lib/status';
import type { MailDispatchSummary, RfpResponses, VendorStageChange } from '../../../types/api';

/**
 * Everything after "Send to Vendors" (POC parts 3-4): the email thread, the quotes read out of
 * the replies, the AI comparison and the follow-up. Read-only except for the two buttons, which
 * reuse the same endpoints the demo drives.
 */

type ResponseTab = 'quotes' | 'evaluation';

const ENGINE_LABEL: Record<string, string> = {
  gemini: 'Gemini',
  heuristic: 'Local reader',
};

/** `3650` + `INR` -> `INR 3,650`; no currency stated -> just the number. */
function money(value: number | null | undefined, currency: string | null): string {
  if (value === null || value === undefined) return '—';
  return `${currency ? `${currency} ` : ''}${formatNumber(value)}`;
}

function stageLabel(change: VendorStageChange): string {
  return change.to === 'completed' ? 'Completed (Stage 4)' : 'Submitted (Stage 3)';
}

export function VendorResponsesPanel({ rfpId }: { rfpId: number }) {
  const { showToast } = useToast();
  const loader = useCallback(() => api.get<RfpResponses>(`/rfps/${rfpId}/responses`), [rfpId]);
  const { data, loading, error, reload } = useAsync(loader, [rfpId]);
  const [tab, setTab] = useState<ResponseTab>('quotes');
  const [busy, setBusy] = useState<'evaluate' | 'remind' | null>(null);

  const evaluate = async () => {
    setBusy('evaluate');
    try {
      const result = await api.post<RfpResponses>(`/rfps/${rfpId}/evaluate`, {});
      const moved = result.stageChanges ?? [];
      const summary =
        moved.length > 0
          ? `${moved.length} vendor(s) moved stage: ${moved.map(stageLabel).join(', ')}.`
          : 'No vendor was ready to move a stage.';
      showToast(`Replies read and offers compared. ${summary}`);
      setTab('evaluation');
      reload();
    } catch (cause) {
      showToast(errorMessage(cause), 'error');
    } finally {
      setBusy(null);
    }
  };

  const remind = async () => {
    setBusy('remind');
    try {
      const mail = await api.post<MailDispatchSummary>(`/rfps/${rfpId}/remind`, {});
      showToast(mailSummaryMessage(mail) ?? 'No vendor needs a reminder.');
      reload();
    } catch (cause) {
      showToast(errorMessage(cause), 'error');
    } finally {
      setBusy(null);
    }
  };

  if (loading && !data) {
    return (
      <section className="panel">
        <div className="panel__body">
          <div className="state-block">
            <Spinner />
          </div>
        </div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className="panel">
        <div className="panel__body">
          <StateBlock
            title="Could not load the vendor responses"
            description={errorMessage(error)}
            action={
              <Button variant="outlined" onClick={reload}>
                Try again
              </Button>
            }
          />
        </div>
      </section>
    );
  }

  const { summary, quotes, evaluation, vendors } = data;

  return (
    <section className="panel">
      <div className="panel__body">
        <div className="responses-header">
          <div>
            <h3 className="detail-section-title">Vendor Responses</h3>
            <p className="text-muted responses-header__summary">
              {summary.quotedVendors} of {vendors.length} vendor(s) quoted · {summary.requestedLines}{' '}
              requested line(s) · {summary.pendingVendors} awaiting a reply
              {summary.overdueVendors > 0
                ? ` · ${summary.overdueVendors} overdue (${summary.reminderAfterDays}+ days)`
                : ''}
            </p>
          </div>
          <div className="responses-header__actions">
            <Button variant="outlined" onClick={remind} disabled={busy !== null}>
              {busy === 'remind' ? 'Sending…' : 'Send Reminders'}
            </Button>
            <Button variant="primary" onClick={evaluate} disabled={busy !== null}>
              {busy === 'evaluate' ? 'Reading…' : 'Read Replies & Compare'}
            </Button>
          </div>
        </div>

        <Tabs
          tabs={[
            { value: 'quotes' as ResponseTab, label: `Quote Comparison (${quotes.vendors.length})` },
            { value: 'evaluation' as ResponseTab, label: 'AI Evaluation' },
          ]}
          value={tab}
          onChange={setTab}
        />

        {tab === 'quotes' ? (
          <div className="quotes">
            {quotes.vendors.length === 0 ? (
              <StateBlock
                title="No offers to compare yet"
                description="When a vendor replies, use “Read Replies & Compare” to read the offer out of it."
              />
            ) : (
              <>
                <div className="quote-cards">
                  {quotes.vendors.map((vendor) => (
                    <div className="quote-card" key={vendor.vendorId}>
                      <div className="quote-card__head">
                        <strong>{vendor.vendorName ?? `Vendor ${vendor.vendorId}`}</strong>
                        <Badge
                          variant={
                            vendor.quotedLines >= vendor.requestedLines ? 'success' : 'warning'
                          }
                        >
                          {vendor.quotedLines}/{vendor.requestedLines} lines
                        </Badge>
                      </div>
                      <p className="quote-card__total">
                        {money(vendor.grandTotal ?? vendor.itemsTotal, vendor.currency)}
                        {vendor.grandTotal !== null ? ' total' : ' items'}
                      </p>
                      <p className="quote-card__facts">
                        {vendor.validity ? `Valid ${vendor.validity}` : 'No validity stated'}
                        {vendor.leadTimeDays !== null
                          ? ` · ${vendor.leadTimeDays} days lead time`
                          : ''}
                        {vendor.paymentTerms ? ` · ${vendor.paymentTerms}` : ''}
                      </p>
                      {vendor.deviations.length > 0 ? (
                        <ul className="quote-card__list">
                          {vendor.deviations.map((deviation) => (
                            <li key={deviation}>{deviation}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ))}
                </div>

                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '30%' }}>Requested line</th>
                        {quotes.vendors.map((vendor) => (
                          <th key={vendor.vendorId}>
                            {vendor.vendorName ?? `Vendor ${vendor.vendorId}`}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {quotes.items.map((row) => (
                        <tr key={row.rfpProductId}>
                          <td>
                            {row.name}
                            <span className="quote-grid__qty">
                              {row.quantity !== null ? ` · ${row.quantity}` : ''}
                              {row.uom ? ` ${row.uom}` : ''}
                            </span>
                          </td>
                          {quotes.vendors.map((vendor) => {
                            const quote = row.quotes.find(
                              (entry) => entry.vendorId === vendor.vendorId,
                            );
                            return (
                              <td key={vendor.vendorId}>
                                {quote ? (
                                  <span className="quote-grid__cell">
                                    <strong>{money(quote.unitPrice, vendor.currency)}</strong>
                                    <span className="quote-grid__line">
                                      {quote.lineTotal !== null
                                        ? `line ${formatNumber(quote.lineTotal)}`
                                        : ''}
                                      {quote.brand ? ` · ${quote.brand}` : ''}
                                    </span>
                                  </span>
                                ) : (
                                  <span className="text-muted">not quoted</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        ) : null}

        {tab === 'evaluation' ? (
          <div className="evaluation">
            {!evaluation ? (
              <StateBlock
                title="No evaluation yet"
                description="Use “Read Replies & Compare” to read the replies and rank the offers."
              />
            ) : (
              <>
                <div className="evaluation__head">
                  <Badge variant={evaluation.engine === 'gemini' ? 'info' : 'neutral-strong'}>
                    {ENGINE_LABEL[evaluation.engine] ?? evaluation.engine}
                    {evaluation.engine === 'gemini' && evaluation.model
                      ? ` · ${evaluation.model}`
                      : ''}
                  </Badge>
                  <Badge
                    variant={
                      evaluation.status === 'ok'
                        ? 'success'
                        : evaluation.status === 'failed'
                          ? 'warning'
                          : 'neutral'
                    }
                  >
                    {evaluation.status.replace('_', ' ')}
                  </Badge>
                  {evaluation.recommendedVendorName ? (
                    <span className="evaluation__recommended">
                      Recommended: <strong>{evaluation.recommendedVendorName}</strong>
                    </span>
                  ) : null}
                  <span className="evaluation__when">
                    {formatDate(evaluation.createdAt)}
                    {evaluation.createdBy ? ` · ${evaluation.createdBy}` : ''}
                  </span>
                </div>

                {evaluation.recommendation ? (
                  <p className="evaluation__recommendation">{evaluation.recommendation}</p>
                ) : null}
                {evaluation.payload?.summary ? (
                  <p className="text-muted">{evaluation.payload.summary}</p>
                ) : null}
                {evaluation.errorMessage ? (
                  <p className="thread__error">Model error: {evaluation.errorMessage}</p>
                ) : null}

                {(evaluation.payload?.ranking ?? []).map((entry) => (
                  <div className="rank-card" key={`${entry.vendorId}-${entry.rank}`}>
                    <div className="rank-card__head">
                      <span className="rank-card__rank">#{entry.rank ?? '—'}</span>
                      <strong>
                        {vendors.find((vendor) => vendor.vendorId === entry.vendorId)?.vendorName ??
                          `Vendor ${entry.vendorId}`}
                      </strong>
                      {entry.score !== null ? <Badge variant="info">Score {entry.score}</Badge> : null}
                    </div>
                    {entry.coverageNote ? <p>{entry.coverageNote}</p> : null}
                    {entry.priceNote ? <p>{entry.priceNote}</p> : null}
                    {entry.strengths.length > 0 ? (
                      <ul className="rank-card__list rank-card__list--good">
                        {entry.strengths.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    ) : null}
                    {entry.risks.length > 0 ? (
                      <ul className="rank-card__list rank-card__list--risk">
                        {entry.risks.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ))}

                <div className="review-grid--spaced">
                  {(evaluation.payload?.missingInformation ?? []).length > 0 ? (
                    <div className="detail-field">
                      <p className="detail-field__label">Missing information</p>
                      <ul className="evaluation__list">
                        {(evaluation.payload?.missingInformation ?? []).map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {(evaluation.payload?.nextSteps ?? []).length > 0 ? (
                    <div className="detail-field">
                      <p className="detail-field__label">Next steps</p>
                      <ul className="evaluation__list">
                        {(evaluation.payload?.nextSteps ?? []).map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </>
            )}
          </div>
        ) : null}

        <div className="table-wrap responses-vendors">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '34%' }}>Vendor</th>
                <th style={{ width: '22%' }}>Stage</th>
                <th style={{ width: '16%' }}>Replied</th>
                <th style={{ width: '16%' }}>Reminder</th>
                <th style={{ width: '12%' }}>Follow-up</th>
              </tr>
            </thead>
            <tbody>
              {vendors.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <StateBlock title="No vendors invited yet" />
                  </td>
                </tr>
              ) : null}
              {vendors.map((vendor) => {
                const meta = RFP_VENDOR_STATUS_META[vendor.status];
                return (
                  <tr key={vendor.vendorId}>
                    <td>
                      {vendor.vendorName}
                      <span className="text-muted"> · {vendor.vendorCode}</span>
                    </td>
                    <td>
                      <Badge variant={meta.variant}>{meta.label}</Badge>
                    </td>
                    <td>{vendor.hasReply ? 'Yes' : 'No'}</td>
                    <td>{vendor.reminderSent ? 'Sent' : '—'}</td>
                    <td>
                      {vendor.overdue ? (
                        <Badge variant="warning">Overdue</Badge>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
